import React, { useState, useEffect } from "react";
import { getAdminProfile, changeAdminPassword } from "../../../APIClient/apiClient";
import Toast from "../../../component/Toast";
import "../../../style/admin/AnnouncementManager.css";

const AdminProfile = ({ adminId }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ message: "", type: "success" });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [language, setLanguage] = useState("en");
  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const translations = {
    en: {
      title: "Admin Profile",
      back: "Back to Dashboard",
      editProfile: "Edit Profile",
      changePassword: "Change Password",
      personalInfo: "Personal Information",
      contactInfo: "Contact Information",
      systemInfo: "System Information",
      fullName: "Full Name",
      username: "Username",
      email: "Email",
      phone: "Phone",
      department: "Department",
      role: "Role",
      createdAt: "Created At",
      lastLogin: "Last Login",
      oldPassword: "Current Password",
      newPassword: "New Password",
      confirmPassword: "Confirm Password",
      passwordRequirements: "Password must be at least 12 characters with uppercase, lowercase, number, and special character",
      save: "Save Changes",
      cancel: "Cancel",
      passwordMismatch: "Passwords do not match",
      passwordChangeSuccess: "Password changed successfully",
      passwordChangeFailed: "Failed to change password",
      loadingProfile: "Loading profile...",
      level1Admin: "Admin Level 1",
      level2Admin: "Admin Level 2",
    },
    vi: {
      title: "Hồ Sơ Quản Trị Viên",
      back: "Quay Lại Dashboard",
      editProfile: "Chỉnh Sửa Hồ Sơ",
      changePassword: "Thay Đổi Mật Khẩu",
      personalInfo: "Thông Tin Cá Nhân",
      contactInfo: "Thông Tin Liên Hệ",
      systemInfo: "Thông Tin Hệ Thống",
      fullName: "Họ và Tên",
      username: "Tên Đăng Nhập",
      email: "Email",
      phone: "Số Điện Thoại",
      department: "Bộ Phận",
      role: "Vai Trò",
      createdAt: "Ngày Tạo",
      lastLogin: "Lần Đăng Nhập Cuối",
      oldPassword: "Mật Khẩu Hiện Tại",
      newPassword: "Mật Khẩu Mới",
      confirmPassword: "Xác Nhận Mật Khẩu",
      passwordRequirements: "Mật khẩu phải có ít nhất 12 ký tự, bao gồm chữ hoa, chữ thường, số và ký tự đặc biệt",
      save: "Lưu Thay Đổi",
      cancel: "Hủy",
      passwordMismatch: "Mật khẩu không khớp",
      passwordChangeSuccess: "Thay đổi mật khẩu thành công",
      passwordChangeFailed: "Không thể thay đổi mật khẩu",
      loadingProfile: "Đang tải hồ sơ...",
      level1Admin: "Quản Trị Viên Cấp 1",
      level2Admin: "Quản Trị Viên Cấp 2",
    },
  };

  const t = translations[language] || translations.en;

  useEffect(() => {
    fetchProfile();
  }, [adminId]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const data = await getAdminProfile(adminId);
      setProfile(data);
    } catch (err) {
      setToast({ message: "Failed to load profile", type: "error" });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setToast({ message: t.passwordMismatch, type: "error" });
      return;
    }

    try {
      await changeAdminPassword(passwordData.oldPassword, passwordData.newPassword);
      setToast({ message: t.passwordChangeSuccess, type: "success" });
      setPasswordData({ oldPassword: "", newPassword: "", confirmPassword: "" });
      setShowPasswordForm(false);
    } catch (err) {
      setToast({ message: err.message || t.passwordChangeFailed, type: "error" });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString(language === "vi" ? "vi-VN" : "en-US");
  };

  const getRoleLabel = (role) => {
    return role === "admin_level_1" ? t.level1Admin : t.level2Admin;
  };

  if (loading) return <div className="admin-manager-loading">{t.loadingProfile}</div>;

  if (!profile) return <div className="admin-manager-loading">Profile not found</div>;

  return (
    <div className="admin-manager">
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "", type: "success" })} />

      <div className="admin-manager-header">
        <h1>{t.title}</h1>
        <div>
          <button onClick={() => setLanguage("en")} style={{ marginRight: "10px", fontWeight: language === "en" ? "bold" : "normal" }}>
            English
          </button>
          <button onClick={() => setLanguage("vi")} style={{ fontWeight: language === "vi" ? "bold" : "normal" }}>
            Tiếng Việt
          </button>
        </div>
      </div>

      <div style={{ maxWidth: "800px", margin: "0 auto", padding: "12px" }}>
        {/* Personal Information */}
        <div style={{ background: "#fff", padding: "20px", borderRadius: "8px", marginBottom: "20px" }}>
          <h3>{t.personalInfo}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "20px" }}>
            <div>
              <label style={{ fontWeight: "bold" }}>{t.fullName}</label>
              <p style={{ marginTop: "5px", color: "#666" }}>{profile.full_name || "N/A"}</p>
            </div>
            <div>
              <label style={{ fontWeight: "bold" }}>{t.username}</label>
              <p style={{ marginTop: "5px", color: "#666" }}>{profile.username}</p>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div style={{ background: "#fff", padding: "20px", borderRadius: "8px", marginBottom: "20px" }}>
          <h3>{t.contactInfo}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "20px" }}>
            <div>
              <label style={{ fontWeight: "bold" }}>{t.email}</label>
              <p style={{ marginTop: "5px", color: "#666" }}>{profile.email || "N/A"}</p>
            </div>
            <div>
              <label style={{ fontWeight: "bold" }}>{t.phone}</label>
              <p style={{ marginTop: "5px", color: "#666" }}>{profile.phone || "N/A"}</p>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ fontWeight: "bold" }}>{t.department}</label>
              <p style={{ marginTop: "5px", color: "#666" }}>{profile.department || "N/A"}</p>
            </div>
          </div>
        </div>

        {/* System Information */}
        <div style={{ background: "#fff", padding: "20px", borderRadius: "8px", marginBottom: "20px" }}>
          <h3>{t.systemInfo}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "20px" }}>
            <div>
              <label style={{ fontWeight: "bold" }}>{t.role}</label>
              <p style={{ marginTop: "5px", color: "#224191", fontWeight: "bold" }}>{getRoleLabel(profile.role)}</p>
            </div>
            <div>
              <label style={{ fontWeight: "bold" }}>{t.createdAt}</label>
              <p style={{ marginTop: "5px", color: "#666", fontSize: "12px" }}>{formatDate(profile.created_at)}</p>
            </div>
            <div>
              <label style={{ fontWeight: "bold" }}>{t.lastLogin}</label>
              <p style={{ marginTop: "5px", color: "#666", fontSize: "12px" }}>{formatDate(profile.last_login)}</p>
            </div>
          </div>
        </div>

        {/* Password Change Section */}
        <div style={{ background: "#fff", padding: "20px", borderRadius: "8px", marginBottom: "20px" }}>
          <button
            className="admin-manager-btn-primary"
            onClick={() => setShowPasswordForm(!showPasswordForm)}
            style={{ marginBottom: showPasswordForm ? "20px" : "0" }}
          >
            {showPasswordForm ? t.cancel : t.changePassword}
          </button>

          {showPasswordForm && (
            <form onSubmit={handlePasswordChange} style={{ marginTop: "20px" }}>
              <div style={{ marginBottom: "15px" }}>
                <label style={{ fontWeight: "bold" }}>{t.oldPassword}</label>
                <input
                  type="password"
                  value={passwordData.oldPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, oldPassword: e.target.value })}
                  placeholder={t.oldPassword}
                  required
                  style={{ width: "100%", padding: "8px", marginTop: "5px", borderRadius: "4px", border: "1px solid #ddd" }}
                />
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label style={{ fontWeight: "bold" }}>{t.newPassword}</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  placeholder={t.newPassword}
                  required
                  style={{ width: "100%", padding: "8px", marginTop: "5px", borderRadius: "4px", border: "1px solid #ddd" }}
                />
                <small style={{ color: "#666", marginTop: "5px", display: "block" }}>{t.passwordRequirements}</small>
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label style={{ fontWeight: "bold" }}>{t.confirmPassword}</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  placeholder={t.confirmPassword}
                  required
                  style={{ width: "100%", padding: "8px", marginTop: "5px", borderRadius: "4px", border: "1px solid #ddd" }}
                />
              </div>

              <button type="submit" className="admin-manager-btn-primary">
                {t.save}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminProfile;
